import { PAGE_SIZE } from './config';

/**
 * Planification PURE du chargement fenêtré (XPHOTO-18, correctif « lignes vides »).
 *
 * La grille virtualisée ne peut disposer (layout justifié) que le PRÉFIXE contigu
 * d'assets chargés : une rangée justifiée a besoin des ratios de TOUTES ses tuiles,
 * donc on ne dispose que jusqu'au premier trou. Conséquence : pour atteindre l'asset
 * #10 000, il faut que les pages 0..99 soient chargées DANS L'ORDRE (chacune étend le
 * préfixe). On charge donc les pages CONTIGUËMENT depuis la frontière chargée.
 *
 * Cette fonction décide, à partir de la frontière chargée (`loadedCount`), de la fin
 * de la fenêtre visible (`visibleEnd`, index absolu observé par la virtualisation) et
 * d'un tampon d'anticipation, QUELLES pages demander à ce tick — bornées à `total` et
 * plafonnées par tick (`maxPagesPerTick`) pour éviter une rafale (429).
 */

/** Offset de début de page contenant l'index (aligné sur `PAGE_SIZE`). */
export function pageStartFor(index: number, pageSize: number = PAGE_SIZE): number {
  if (index <= 0) return 0;
  return Math.floor(index / pageSize) * pageSize;
}

export interface PlanPagesInput {
  /** Nombre d'assets CONTIGUS déjà chargés (frontière du préfixe dense). */
  loadedCount: number;
  /** Index absolu (exclusif) de la fin de la fenêtre visible souhaitée (visible + tampon). */
  targetEnd: number;
  /** Total d'assets de la requête (borne haute). */
  total: number;
  /** Offsets de pages déjà demandées / en vol (à ne pas re-demander). */
  requested: ReadonlySet<number>;
  /** Taille de page (offsets alignés). */
  pageSize?: number;
  /** Nombre max de pages nouvelles par tick (anti-rafale). */
  maxPagesPerTick?: number;
}

/**
 * Retourne les OFFSETS de page à charger à ce tick.
 *
 * Règle : on charge CONTIGUËMENT depuis la frontière chargée (`loadedCount`) — c'est
 * indispensable car le layout justifié ne dispose que le préfixe dense — jusqu'à
 * couvrir `targetEnd` (fin de fenêtre visible + tampon), sans jamais dépasser `total`,
 * en sautant les pages déjà demandées, et en plafonnant le nombre de NOUVELLES pages
 * par tick (les suivantes seront demandées aux ticks ultérieurs, au fil du défilement).
 *
 * Déterministe et sans effet de bord → testable sans DOM.
 */
export function planPages(input: PlanPagesInput): number[] {
  const {
    loadedCount,
    targetEnd,
    total,
    requested,
    pageSize = PAGE_SIZE,
    maxPagesPerTick = 3,
  } = input;

  if (total <= 0 || pageSize <= 0) return [];

  // On veut couvrir au moins jusqu'à `targetEnd` (borné au total), et TOUJOURS étendre
  // le préfixe chargé (sinon la grille reste bloquée à `loadedCount`).
  const want = Math.min(total, Math.max(targetEnd, loadedCount + 1));

  // Première page potentiellement manquante : celle qui contient la frontière chargée.
  const firstOffset = pageStartFor(loadedCount, pageSize);
  const lastOffset = pageStartFor(Math.max(0, want - 1), pageSize);

  const offsets: number[] = [];
  for (let offset = firstOffset; offset <= lastOffset; offset += pageSize) {
    if (offset >= total) break;
    if (requested.has(offset)) continue;
    offsets.push(offset);
    if (offsets.length >= maxPagesPerTick) break;
  }
  return offsets;
}
